import { useCallback } from 'react';
import { getDocumentById, getChatById, getVoiceChannelById, getSpreadsheetById, getPresentationById, getAllSpaceItems, getProjectIdByName as getSpaceIdByName } from '@/db/projects';
import { useAppStore } from '@/store/appStore';
import type { ItemType } from '@/store/appStore';
import { cacheSpaceItems, isIndexedDBAvailable } from '@/utils/structureCache';
import { handleError } from '@/utils/errorHandler';

/**
* Hook per gestire la navigazione tra diversi tipi di item (documenti, chat, voice channels, etc.).
* Fornisce funzioni per caricare un item specifico e per aggiornare gli item di uno space.
* 
* @returns Oggetto con funzioni per gestire la navigazione degli item
* @returns handleItemClick - Carica e imposta l'item corrente nello store
* @returns refreshSpaceItems - Ricarica tutti gli item di uno space specifico
*/
export function useItemNavigation() {
  const setCurrentItem = useAppStore((state) => state.setCurrentItem);
  const setIsLoadingItem = useAppStore((state) => state.setIsLoadingItem);
  const updateSpaceItems = useAppStore((state) => state.updateSpaceItems);
  const setFolderMappings = useAppStore((state) => state.setFolderMappings);
  const spaces = useAppStore((state) => state.spaces);
  const folderMappings = useAppStore((state) => state.folderMappings);

  const handleItemClick = useCallback(async (type: ItemType, id: string, urlPath?: string | null) => {
  setIsLoadingItem(true);
  try {
      let item = null;

      if (type === 'document') {
    item = await getDocumentById(id);
      } else if (type === 'chat') {
    item = await getChatById(id);
      } else if (type === 'voice_channel') {
    item = await getVoiceChannelById(id);
      } else if (type === 'spreadsheet') {
    item = await getSpreadsheetById(id);
      } else if (type === 'presentation') {
    item = await getPresentationById(id);
      }

      if (item) {
    // Use URL path if provided, otherwise calculate from item's parentId
    let path: string | null = urlPath ?? null;

    if (path === null && item.parent_id) {
          const parentIdStr = String(item.parent_id);
          // Check if parentId is a space (root)
          const space = spaces.find(s => s.id === parentIdStr);
          if (space) {
      path = space.name;
          } else {
      // Check if parentId is a folder
      // Find which space this folder belongs to by checking folderMappings
      for (const [spaceName, folderMapping] of Object.entries(folderMappings)) {
              const folderName = Object.entries(folderMapping).find(([, folderId]) => String(folderId) === parentIdStr)?.[0];
              if (folderName) {
        path = `${spaceName}/${folderName}`;
        break;
              }
      }
          }
    }

    setCurrentItem(type, item, path);
      }
  } catch (error) {
      handleError(error, { level: 'error', message: 'Errore nel caricamento dell\'elemento' });
  } finally {
      setIsLoadingItem(false);
  }
  }, [setCurrentItem, setIsLoadingItem, spaces, folderMappings]);

  const refreshSpaceItems = useCallback(async (spaceName: string) => {
  const spaceId = await getSpaceIdByName(spaceName);
  if (!spaceId) return;

  try {
      // Use getAllSpaceItems for complete refresh (includes folders)
      const result = await getAllSpaceItems(spaceId);

      // Update store with all items (unified structure)
      updateSpaceItems(spaceName, result.items);

      // Update folder mappings
      const folderMapping: Record<string, string> = {};
      for (const folder of result.folders) {
    folderMapping[folder.name] = folder.id;
      }
      const currentMappings = useAppStore.getState().folderMappings;
      setFolderMappings({
    ...currentMappings,
    [spaceName]: folderMapping,
      });

      // Update cache to keep it in sync
      if (isIndexedDBAvailable()) {
    await cacheSpaceItems(spaceId, {
          items: result.items.map((item) => ({
      id: item.id,
      name: item.name,
      type: item.type,
      content: item.content,
      parent_id: item.parent_id,
          })),
          folders: result.folders.map((f) => ({ id: f.id, name: f.name })),
    }).catch((err) => handleError(err, { level: 'warning', message: 'Errore nella cache degli elementi dello spazio' }));
      }
  } catch (error) {
      handleError(error, { level: 'warning', message: 'Errore nell\'aggiornamento degli elementi dello spazio' });
  }
  }, [updateSpaceItems, setFolderMappings]);

  return {
  handleItemClick,
  refreshSpaceItems,
  };
}

